import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Truck, Search, Shield, MapPin, Package, BarChart3, Zap, Globe2 } from "lucide-react";

export default function PublicHome() {
  const [lr, setLr] = useState("");
  const nav = useNavigate();
  const submit = (e) => { e.preventDefault(); if (lr.trim()) nav(`/track/${lr.trim().toUpperCase()}`); };

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="bg-[#1E3A8A] w-9 h-9 rounded-md grid place-items-center text-white"><Truck size={18}/></div>
            <div className="font-bold text-lg text-slate-900">Ritik Cargo Carriers</div>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm text-slate-700">
            <a href="#services" className="hover:text-sky-700">Services</a>
            <a href="#why" className="hover:text-sky-700">Why us</a>
            <a href="#track" className="hover:text-sky-700">Track</a>
            <a href="#contact" className="hover:text-sky-700">Contact</a>
          </div>
          <Link to="/login" data-testid="home-login" className="lt-btn-primary px-4 py-2 rounded-md text-sm font-medium">Staff Login</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-gradient text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-xs mb-5 backdrop-blur"><Zap size={13}/> Trusted by 500+ logistics SMEs</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">Run your logistics business with one platform.</h1>
            <p className="mt-5 text-sky-100 text-lg max-w-xl">Bookings, real-time tracking, branches, vehicles, agents, reports — Ritik Cargo Carriers moves your freight faster, with paperwork that runs itself.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/login" className="bg-white text-[#1E3A8A] hover:bg-sky-50 px-6 py-3 rounded-md font-semibold">Get Started</Link>
              <a href="#track" className="bg-white/15 hover:bg-white/25 px-6 py-3 rounded-md font-semibold backdrop-blur">Track a Shipment</a>
            </div>
          </div>
          <div className="lt-card p-6 bg-white text-slate-900" id="track">
            <h3 className="font-bold text-lg mb-1">Track your consignment</h3>
            <p className="text-sm text-slate-500 mb-4">Enter your LR Number for live status</p>
            <form onSubmit={submit} className="flex gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input data-testid="home-track-input" value={lr} onChange={e=>setLr(e.target.value)} placeholder="LR2026000001"
                  className="w-full pl-9 pr-3 py-3 border border-slate-200 rounded-md uppercase tracking-wide focus:outline-none focus:border-sky-500"/>
              </div>
              <button type="submit" data-testid="home-track-submit" className="lt-btn-primary px-5 rounded-md font-semibold">Track</button>
            </form>
            <div className="mt-3 text-xs text-slate-500">Tip: Sample LRs are visible after login on the dashboard.</div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="text-sky-700 text-sm font-semibold uppercase tracking-widest">What we deliver</div>
          <h2 className="text-3xl sm:text-4xl font-bold mt-2 text-slate-900">Built for the long haul</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {icon:Package, title:"Consignment Booking", desc:"Auto LR generation, freight, invoice, multi-stop with one form."},
            {icon:MapPin, title:"Live Tracking", desc:"Branch-to-branch tracking with a customer-facing public timeline."},
            {icon:Truck, title:"Fleet & Drivers", desc:"Manage vehicles, drivers, insurance and fitness expiries."},
            {icon:Globe2, title:"Multi-Branch Network", desc:"Operate across cities and states from a single dashboard."},
            {icon:BarChart3, title:"Reports & Analytics", desc:"Revenue, agent performance, delivery SLAs — exportable."},
            {icon:Shield, title:"Multi-Tenant SaaS", desc:"Each company sees only its data; role-based access enforced."},
          ].map((s,i) => (
            <div key={i} className="lt-card p-6 hover:shadow-md transition">
              <div className="w-11 h-11 rounded-md bg-[#1E3A8A]/10 text-[#1E3A8A] grid place-items-center mb-4"><s.icon size={22}/></div>
              <h3 className="font-bold text-slate-900">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section id="why" className="bg-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            ["10k+","Shipments / month"],
            ["99.5%","Uptime SLA"],
            ["28","Indian states served"],
            ["4.8★","Operator rating"],
          ].map(([n,l]) => (
            <div key={l} className="lt-card p-6 text-center">
              <div className="text-4xl font-bold text-[#1E3A8A]">{n}</div>
              <div className="text-sm text-slate-600 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA / Contact */}
      <section id="contact" className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Talk to us</h2>
          <p className="text-slate-600 mt-3">Need reliable cargo movement or want a demo of our operations platform? Our team is a call away.</p>
          <ul className="mt-5 text-sm text-slate-700 space-y-2">
            <li><b>Email:</b> hello@ritikcargo.com</li>
            <li><b>Phone:</b> +91 98765 43210</li>
            <li><b>Office:</b> Mumbai · Delhi · Bangalore</li>
          </ul>
        </div>
        <form onSubmit={(e)=>{e.preventDefault(); alert("Thanks! We will get in touch.");}} className="lt-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
          <input required placeholder="Name" className="px-3 py-2 border border-slate-200 rounded-md md:col-span-2"/>
          <input required type="email" placeholder="Email" className="px-3 py-2 border border-slate-200 rounded-md"/>
          <input placeholder="Phone" className="px-3 py-2 border border-slate-200 rounded-md"/>
          <textarea required placeholder="Message" rows={4} className="px-3 py-2 border border-slate-200 rounded-md md:col-span-2"/>
          <button type="submit" className="lt-btn-primary px-5 py-2.5 rounded-md font-semibold md:col-span-2">Send</button>
        </form>
      </section>

      <footer className="border-t bg-slate-900 text-slate-300 py-8 text-center text-sm">© {new Date().getFullYear()} Ritik Cargo Carriers. All rights reserved.</footer>
    </div>
  );
}
